import {createSlice} from '@reduxjs/toolkit'
import {createApi} from '@reduxjs/toolkit/query/react'

import {baseQuery} from './auth'
import {
  mapSupportMessage,
  mapSupportTicket,
  mapSupportTicketsResponse,
} from '../components/cabinet/support/support-helpers'

export const supportApi = createApi({
  reducerPath: 'supportApi',
  baseQuery: baseQuery(),
  endpoints: (builder) => ({
    createSupportTicket: builder.mutation<SupportTicket, SupportTicketCreateRequest>({
      query: ({attachments, ...body}) => {
        if (!attachments.length) {
          return {
            url: 'support/v4.0/tickets/',
            method: 'POST',
            body,
          }
        }

        const formData = new FormData();

        Object.entries(body).forEach(([ key, value ]) => formData.append(key, value));
        attachments.forEach((file) => formData.append('attachments', file));

        return {
          url: 'support/v4.0/tickets/',
          method: 'POST',
          body: formData,
        }
      },
    }),
    createSupportTicketMessage: builder.mutation<SupportTicketMessage, SupportTicketMessageCreateRequest>({
      query: ({attachments, ticketId, text}) => {
        if (!attachments.length) {
          return {
            url: `support/v4.0/tickets/${ticketId}/messages/`,
            method: 'POST',
            body: {text},
          }
        }

        const formData = new FormData();

        formData.append('text', text);
        attachments.forEach((file) => formData.append('attachments', file));

        return {
          url: `support/v4.0/tickets/${ticketId}/messages/`,
          method: 'POST',
          body: formData,
        }
      },
    }),
    getSupportTickets: builder.query<SupportTicketsResponse, SupportTicketsRequest>({
      query: (params) => ({
        url: 'support/v4.0/tickets/',
        params,
      }),
    }),
    getSupportTicket: builder.query<SupportTicket, string>({
      query: (id) => `support/v4.0/tickets/${id}/`,
    }),
    getSupportStatuses: builder.query<SupportStatusItem[], void>({
      query: () => 'support/v4.0/meta/statuses/',
    }),
    getSupportUnreadCount: builder.query<SupportUnreadCountResponse, void>({
      query: () => 'support/v4.0/unread-count/',
    }),
    getSupportSteps: builder.query<SupportStepItem[], SupportStepsRequest>({
      query: ({category}) => ({
        url: 'support/v4.0/meta/steps/',
        params: {category},
      }),
    }),
  }),
})

export const {
  useCreateSupportTicketMutation,
  useCreateSupportTicketMessageMutation,
  useGetSupportTicketQuery,
  useGetSupportStatusesQuery,
  useGetSupportStepsQuery,
  useGetSupportUnreadCountQuery,
  useLazyGetSupportTicketsQuery,
} = supportApi

export const supportSlice = createSlice({
  name: 'support',
  initialState: {
    statuses: [],
    steps: [],
    unreadCount: 0,
  } as SupportState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      supportApi.endpoints.createSupportTicket.matchFulfilled,
      (state, {payload}) => {
        state.createdTicket = mapSupportTicket(payload)
      }
    )
    builder.addMatcher(
      supportApi.endpoints.createSupportTicketMessage.matchFulfilled,
      (state, {payload, meta: {arg: {originalArgs}}}) => {
        if (state.ticket?.id !== originalArgs.ticketId) {
          return;
        }

        const message = mapSupportMessage(payload);

        state.ticket.messages = [ message, ...(state.ticket.messages || []) ];
        state.ticket.lastMessage = message.text[message.text.length - 1] || state.ticket.lastMessage;
        state.ticket.lastMessageAuthor = message.author;
      }
    )
    builder.addMatcher(
      supportApi.endpoints.getSupportTicket.matchFulfilled,
      (state, {payload}) => {
        state.ticket = mapSupportTicket(payload)
      }
    )
    builder.addMatcher(
      supportApi.endpoints.getSupportTickets.matchFulfilled,
      (state, {payload, meta: {arg: {originalArgs}}}) => {
        state.tickets = {
          data: mapSupportTicketsResponse(payload),
          params: originalArgs,
        }
      }
    )
    builder.addMatcher(
      supportApi.endpoints.getSupportStatuses.matchFulfilled,
      (state, {payload}) => {
        state.statuses = payload
      }
    )
    builder.addMatcher(
      supportApi.endpoints.getSupportUnreadCount.matchFulfilled,
      (state, {payload}) => {
        state.unreadCount = payload.unread_count
      }
    )
    builder.addMatcher(
      supportApi.endpoints.getSupportSteps.matchFulfilled,
      (state, {payload}) => {
        state.steps = payload
      }
    )
  },
})
